import { motion } from "framer-motion";
import { Phone, MapPin, Clock, Shield, Mail } from "lucide-react";
import { useEffect } from "react";

const PHONE = "5599996208";
const PHONE_DISPLAY = "(559) 999-6208";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

export default function ContactPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="overflow-hidden">
      {/* ═══ HERO ═══ */}
      <section className="relative py-32 md:py-40 bg-pool-deep overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://customer-assets.emergentagent.com/job_petes-pool-fresno/artifacts/p7u60kcc_Screenshot%202026-03-05%20at%207.23.05%E2%80%AFPM.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-pool-deep/80" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-pool-aqua text-sm font-bold tracking-[0.2em] uppercase mb-4">
              Get In Touch
            </p>
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white tracking-tight mb-6">
              Contact Us
            </h1>
            <p className="text-white/60 text-lg md:text-xl max-w-2xl mx-auto">
              Ready for cleaner water? Give us a call or send us an email to
              schedule your pool service.
            </p>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-10">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0,40 C360,80 720,0 1080,40 C1260,60 1380,50 1440,40 L1440,80 L0,80 Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* ═══ CONTACT CONTENT ═══ */}
      <section data-testid="contact-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* LEFT - Main Contact Info */}
            <motion.div {...fadeUp}>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-pool-deep tracking-tight mb-4">
                Let's Talk About
                <br />
                <span className="text-pool-aqua">Your Pool</span>
              </h2>
              <p className="text-slate-500 text-base md:text-lg leading-relaxed mb-10">
                The best way to get started is to give us a call or send an email.
                We'll discuss your pool needs and schedule a visit at a time
                that works for you.
              </p>

              {/* Phone - Large CTA */}
              <a
                href={`tel:${PHONE}`}
                data-testid="contact-phone-large"
                className="group flex items-center gap-5 p-6 rounded-2xl bg-pool-aqua/5 border border-pool-aqua/15 hover:bg-pool-aqua/10 transition-all duration-300 mb-5"
              >
                <div className="w-16 h-16 rounded-2xl bg-pool-aqua/15 flex items-center justify-center group-hover:bg-pool-aqua/25 transition-colors">
                  <Phone size={28} className="text-pool-aqua" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1">
                    Call Us Now
                  </p>
                  <p className="text-3xl font-bold text-pool-deep">{PHONE_DISPLAY}</p>
                </div>
              </a>

              {/* Email CTA */}
              <a
                href="mailto:petespools6208@gmail.com"
                data-testid="contact-email"
                className="group flex items-center gap-5 p-6 rounded-2xl bg-gold/5 border border-gold/15 hover:bg-gold/10 transition-all duration-300 mb-8"
              >
                <div className="w-16 h-16 rounded-2xl bg-gold/15 flex items-center justify-center group-hover:bg-gold/25 transition-colors">
                  <Mail size={28} className="text-gold" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1">
                    Email Us
                  </p>
                  <p className="text-lg font-semibold text-pool-deep break-all">
                    petespools6208@gmail.com
                  </p>
                </div>
              </a>

              {/* Se Habla Español */}
              <div className="p-5 rounded-2xl bg-pool-sand/50 border border-pool-sand">
                <p className="font-accent italic text-lg text-pool-deep">
                  Se Habla Español
                </p>
                <p className="text-slate-500 text-sm mt-1">
                  We proudly serve our Spanish-speaking community.
                </p>
              </div>
            </motion.div>

            {/* RIGHT - Details */}
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              <div className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-pool-deep/5 p-8 md:p-10">
                <h3 className="font-heading text-2xl font-bold text-pool-deep mb-8">
                  Business Details
                </h3>

                <div className="space-y-6">
                  {/* Service Area */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-pool-aqua/10 flex items-center justify-center flex-shrink-0">
                      <MapPin size={20} className="text-pool-aqua" />
                    </div>
                    <div>
                      <p className="font-semibold text-pool-deep mb-1">Service Area</p>
                      <p className="text-slate-500 text-sm">Fresno, CA</p>
                      <p className="text-slate-500 text-sm">Clovis, CA</p>
                      <p className="text-slate-500 text-sm">Fresno County</p>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-pool-aqua/10 flex items-center justify-center flex-shrink-0">
                      <Clock size={20} className="text-pool-aqua" />
                    </div>
                    <div>
                      <p className="font-semibold text-pool-deep mb-1">Hours</p>
                      <p className="text-slate-500 text-sm">Monday - Saturday</p>
                      <p className="text-slate-500 text-sm">7:00 AM - 6:00 PM</p>
                    </div>
                  </div>

                  {/* BBB */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center flex-shrink-0">
                      <Shield size={20} className="text-gold" />
                    </div>
                    <div>
                      <p className="font-semibold text-pool-deep mb-1">A+ BBB Rating</p>
                      <p className="text-slate-500 text-sm">Better Business Bureau Accredited</p>
                    </div>
                  </div>

                  {/* Owner */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-pool-aqua/10 flex items-center justify-center flex-shrink-0">
                      <Phone size={20} className="text-pool-aqua" />
                    </div>
                    <div>
                      <p className="font-semibold text-pool-deep mb-1">Owner-Operated</p>
                      <p className="text-slate-500 text-sm">Pete Cervantes</p>
                      <p className="text-slate-500 text-sm">15+ Years of Experience</p>
                      <p className="text-slate-500 text-sm">6 Years as Owner</p>
                    </div>
                  </div>
                </div>

                {/* How It Works */}
                <div className="mt-10 pt-8 border-t border-slate-100">
                  <h4 className="text-sm font-bold tracking-[0.15em] uppercase text-pool-deep/40 mb-6">
                    How It Works
                  </h4>
                  <div className="space-y-4">
                    {[
                      { step: "1", text: "Call or email us to discuss your pool needs" },
                      { step: "2", text: "We'll schedule a visit at a convenient time" },
                      { step: "3", text: "Pete arrives and takes care of your pool" },
                    ].map((item) => (
                      <div key={item.step} className="flex items-center gap-4">
                        <div className="w-8 h-8 rounded-full bg-pool-aqua/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-pool-aqua text-sm font-bold">{item.step}</span>
                        </div>
                        <p className="text-slate-600 text-sm">{item.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ MAP ═══ */}
      <section data-testid="contact-map-section" className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-10">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-pool-deep tracking-tight">
              Our Service Area
            </h2>
            <p className="text-slate-500 mt-2">
              Serving all of Fresno & Clovis, California
            </p>
          </motion.div>
          <motion.div {...fadeUp}>
            <div className="rounded-2xl overflow-hidden shadow-xl shadow-pool-deep/10 border border-slate-100">
              <iframe
                title="Pete's Pool Service Area - Fresno and Clovis"
                src="https://www.google.com/maps/embed?pb=!1m16!1m12!1m3!1d103000!2d-119.82!3d36.79!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!2m1!1sFresno+and+Clovis+CA!5e0!3m2!1sen!2sus!4v1709669000000!5m2!1sen!2sus"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-google-maps"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ EMERGENCY CTA ═══ */}
      <section className="py-16 bg-pool-deep">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div {...fadeUp}>
            <p className="text-gold text-sm font-bold tracking-[0.2em] uppercase mb-3">
              Emergency Pool Service
            </p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
              Pool Emergency? Call Now.
            </h2>
            <p className="text-white/60 text-base mb-8 max-w-xl mx-auto">
              Equipment failure, green pool, or urgent repair? We respond quickly
              to keep your pool safe.
            </p>
            <a
              href={`tel:${PHONE}`}
              data-testid="emergency-call"
              className="inline-flex items-center gap-3 bg-gold text-pool-deep px-10 py-5 rounded-full text-lg font-bold transition-all duration-300 hover:shadow-xl hover:shadow-gold/30 hover:-translate-y-1"
            >
              <Phone size={22} />
              {PHONE_DISPLAY}
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
